/**
 * Comprehensive API Error Handler with Intelligent Error Categorization
 * Provides production-ready error handling and monitoring for API migration
 */

import { AxiosError, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { logger } from '../../utils/logger';

/**
 * Enhanced error categories for intelligent error management
 */
export enum APIErrorCategory {
  NETWORK_ERROR = 'network',
  SERVER_ERROR = 'server', 
  CLIENT_ERROR = 'client',
  TIMEOUT = 'timeout',
  RATE_LIMIT = 'rate_limit',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  MAINTENANCE = 'maintenance',
  CIRCUIT_BREAKER = 'circuit_breaker',
  UNKNOWN = 'unknown'
}

/**
 * Error severity levels for monitoring and alerting
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

const apiErrorCategoryValues: ReadonlySet<string> = new Set(Object.values(APIErrorCategory));

function isAPIErrorCategory(value: string): value is APIErrorCategory {
  return apiErrorCategoryValues.has(value);
}

/**
 * Error context information for enhanced monitoring
 */
export interface APIErrorContext {
  endpoint: string;
  method: string;
  requestId?: string;
  userId?: string;
  establishmentId?: string;
  userAgent?: string;
  timestamp: Date;
  retryCount?: number;
  fallbackUsed?: boolean;
  responseTime?: number;
}

/**
 * Enhanced error information for monitoring and decision making
 */
export interface APIErrorInfo {
  category: APIErrorCategory;
  severity: ErrorSeverity;
  code: string | number;
  message: string;
  userMessage: string;
  shouldRetry: boolean;
  shouldFallback: boolean;
  retryDelay?: number;
  context: APIErrorContext;
  originalError: AxiosError | Error;
  metadata?: Record<string, unknown>;
}

/**
 * Error handling strategy configuration
 */
export interface ErrorHandlingStrategy {
  maxRetries: number;
  retryDelayMs: number;
  exponentialBackoff: boolean;
  enableFallback: boolean;
  enableUserNotification: boolean;
  enableLogging: boolean;
  enableMetrics: boolean;
}

/**
 * Circuit breaker configuration for preventing cascade failures
 */
export interface CircuitBreakerConfig {
  failureThreshold: number;
  timeout: number;
  resetTimeout: number;
}

/**
 * Circuit breaker state tracking
 */
interface CircuitBreakerState {
  failures: number;
  lastFailureTime: number;
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
}

/**
 * Comprehensive API Error Handler
 */
export class APIErrorHandler {
  private static instance: APIErrorHandler;
  private circuitBreakers: Map<string, CircuitBreakerState> = new Map();
  private errorStats: Map<string, { count: number; lastOccurrence: Date }> = new Map();
  private strategy: ErrorHandlingStrategy;
  private circuitBreakerConfig: CircuitBreakerConfig;

  constructor(
    strategy?: Partial<ErrorHandlingStrategy>,
    circuitBreakerConfig?: Partial<CircuitBreakerConfig>
  ) {
    this.strategy = {
      maxRetries: 3,
      retryDelayMs: 1000,
      exponentialBackoff: true,
      enableFallback: true,
      enableUserNotification: true,
      enableLogging: true,
      enableMetrics: true,
      ...strategy
    };

    this.circuitBreakerConfig = {
      failureThreshold: 5,
      timeout: 60000, // 1 minute
      resetTimeout: 300000, // 5 minutes
      ...circuitBreakerConfig
    };
  }

  /**
   * Get singleton instance
   */
  static getInstance(): APIErrorHandler {
    if (!APIErrorHandler.instance) {
      APIErrorHandler.instance = new APIErrorHandler();
    }
    return APIErrorHandler.instance;
  }

  /**
   * Main error handling method with comprehensive categorization
   */
  handleError(error: AxiosError | Error, context: Partial<APIErrorContext>): APIErrorInfo {
    const fullContext: APIErrorContext = {
      endpoint: 'unknown',
      method: 'unknown',
      timestamp: new Date(),
      ...context
    };

    const errorInfo = this.categorizeError(error, fullContext);

    // Update circuit breaker state
    this.updateCircuitBreaker(fullContext.endpoint, errorInfo);

    // Update error statistics
    if (this.strategy.enableMetrics) {
      this.updateErrorStats(errorInfo);
    }

    // Log error for monitoring
    if (this.strategy.enableLogging) {
      this.logError(errorInfo);
    }

    // Show user notification if enabled
    if (this.strategy.enableUserNotification) {
      this.notifyUser(errorInfo);
    }

    return errorInfo;
  }

  /**
   * Intelligent error categorization with enhanced logic
   */
  private categorizeError(error: AxiosError | Error, context: APIErrorContext): APIErrorInfo {
    let category = APIErrorCategory.UNKNOWN;
    let severity = ErrorSeverity.MEDIUM;
    let code: string | number = 'UNKNOWN';
    let message = error.message || 'Unknown error';
    let userMessage = 'Ocorreu um erro inesperado. Por favor, tente novamente.';
    let shouldRetry = false;
    let shouldFallback = false;
    let retryDelay: number | undefined;
    const metadata: Record<string, unknown> = {};

    // Check circuit breaker state first
    const circuitState = this.getCircuitBreakerState(context.endpoint);
    if (circuitState === 'OPEN') {
      return {
        category: APIErrorCategory.CIRCUIT_BREAKER,
        severity: ErrorSeverity.HIGH,
        code: 'CIRCUIT_OPEN',
        message: 'Circuit breaker is open',
        userMessage: 'Serviço temporariamente indisponível. Tentando alternativa...',
        shouldRetry: false,
        shouldFallback: true,
        context,
        originalError: error,
        metadata: { circuitState }
      };
    }

    if (this.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const response = axiosError.response;
      const request = axiosError.request;

      // Network/Connection Errors (no response received)
      if (!response && request) {
        if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
          category = APIErrorCategory.TIMEOUT;
          severity = ErrorSeverity.MEDIUM;
          code = axiosError.code;
          message = 'Request timeout';
          userMessage = 'O servidor demorou para responder. Tentando novamente...';
          shouldRetry = true;
          shouldFallback = true;
          retryDelay = this.calculateRetryDelay(context.retryCount || 0);
        } else {
          category = APIErrorCategory.NETWORK_ERROR;
          severity = ErrorSeverity.HIGH;
          code = axiosError.code || 'NETWORK_ERROR';
          message = 'Network connection failed';
          userMessage = 'Problema de conexão. Verificando alternativas...';
          shouldRetry = true;
          shouldFallback = true;
          retryDelay = this.calculateRetryDelay(context.retryCount || 0);
        }
      }
      // Response received but with error status
      else if (response) {
        const status = response.status;
        code = status;

        switch (status) {
          case 400:
            category = APIErrorCategory.CLIENT_ERROR;
            severity = ErrorSeverity.LOW;
            message = 'Bad request';
            userMessage = 'Dados inválidos na requisição.';
            shouldRetry = false;
            shouldFallback = false;
            break;

          case 401:
            category = APIErrorCategory.AUTHENTICATION;
            severity = ErrorSeverity.HIGH;
            message = 'Authentication failed';
            userMessage = 'Sessão expirada. Por favor, faça login novamente.';
            shouldRetry = false;
            shouldFallback = false;
            metadata['requiresReauth'] = true;
            break;

          case 403:
            category = APIErrorCategory.AUTHORIZATION;
            severity = ErrorSeverity.MEDIUM;
            message = 'Access forbidden';
            userMessage = 'Acesso negado. Você não tem permissão para esta ação.';
            shouldRetry = false;
            shouldFallback = false;
            break;

          case 404:
            category = APIErrorCategory.NOT_FOUND;
            severity = ErrorSeverity.LOW;
            message = 'Resource not found';
            userMessage = 'Recurso não encontrado.';
            shouldRetry = false;
            shouldFallback = true; // May be available in mock
            break;

          case 409:
            category = APIErrorCategory.CONFLICT;
            severity = ErrorSeverity.MEDIUM;
            message = 'Conflict';
            userMessage = 'Conflito nos dados. Outro usuário pode ter modificado o registro.';
            shouldRetry = false;
            shouldFallback = false;
            break;

          case 422:
            category = APIErrorCategory.VALIDATION;
            severity = ErrorSeverity.LOW;
            message = 'Validation failed';
            userMessage = 'Erro de validação. Verifique os dados informados.';
            shouldRetry = false;
            shouldFallback = false;
            metadata['validationErrors'] = response.data;
            break;

          case 429:
            category = APIErrorCategory.RATE_LIMIT;
            severity = ErrorSeverity.MEDIUM;
            message = 'Rate limit exceeded';
            userMessage = 'Muitas requisições. Aguardando para tentar novamente...';
            shouldRetry = true;
            shouldFallback = true;
            retryDelay = this.extractRetryAfter(response) || 5000;
            break;

          case 500:
          case 502:
          case 503:
          case 504:
            category = APIErrorCategory.SERVER_ERROR;
            severity = status === 503 ? ErrorSeverity.HIGH : ErrorSeverity.CRITICAL;
            message = 'Server error';
            userMessage = 'Erro no servidor. Tentando alternativa...';
            shouldRetry = true;
            shouldFallback = true;
            retryDelay = this.calculateRetryDelay(context.retryCount || 0);
            
            if (status === 503) {
              category = APIErrorCategory.MAINTENANCE;
              userMessage = 'Servidor em manutenção. Usando dados locais...';
            }
            break;

          default:
            if (status >= 500) {
              category = APIErrorCategory.SERVER_ERROR;
              severity = ErrorSeverity.HIGH;
              shouldRetry = true;
              shouldFallback = true;
            } else if (status >= 400) {
              category = APIErrorCategory.CLIENT_ERROR;
              severity = ErrorSeverity.LOW;
              shouldRetry = false;
              shouldFallback = false;
            }
        }
      }
      // Request was never sent
      else {
        category = APIErrorCategory.NETWORK_ERROR;
        severity = ErrorSeverity.HIGH;
        message = 'Request failed to send';
        userMessage = 'Erro de conectividade. Verificando alternativas...';
        shouldRetry = true;
        shouldFallback = true;
      }
    } else {
      // Non-Axios errors (JavaScript errors, etc.)
      category = APIErrorCategory.UNKNOWN;
      severity = ErrorSeverity.MEDIUM;
      code = error.name || 'JS_ERROR';
      message = error.message;
      userMessage = 'Erro interno. Por favor, recarregue a página.';
      shouldRetry = false;
      shouldFallback = false;
    }

    return {
      category,
      severity,
      code,
      message,
      userMessage,
      shouldRetry,
      shouldFallback,
      retryDelay,
      context,
      originalError: error,
      metadata
    };
  }

  /**
   * Check if error is an Axios error
   */
  private isAxiosError(error: unknown): error is AxiosError {
    return typeof error === 'object' && error !== null && 'isAxiosError' in error && (error as AxiosError).isAxiosError === true;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(retryCount: number): number {
    if (!this.strategy.exponentialBackoff) {
      return this.strategy.retryDelayMs;
    }

    const baseDelay = this.strategy.retryDelayMs;
    const exponentialDelay = baseDelay * Math.pow(2, retryCount);
    const jitter = Math.random() * 1000; // Add jitter to prevent thundering herd
    
    return Math.min(exponentialDelay + jitter, 30000); // Cap at 30 seconds
  }

  /**
   * Extract retry-after header value
   */
  private extractRetryAfter(response: AxiosResponse): number | null {
    const retryAfter = response.headers['retry-after'];
    if (retryAfter) {
      const seconds = parseInt(retryAfter, 10);
      return isNaN(seconds) ? null : seconds * 1000;
    }
    return null;
  }

  /**
   * Circuit breaker implementation
   */
  private updateCircuitBreaker(endpoint: string, errorInfo: APIErrorInfo): void {
    const key = `${endpoint}:${errorInfo.category}`;
    const state = this.circuitBreakers.get(key) || {
      failures: 0,
      lastFailureTime: 0,
      state: 'CLOSED' as const
    };

    const now = Date.now();

    // Reset if enough time has passed
    if (state.state === 'OPEN' && 
        now - state.lastFailureTime > this.circuitBreakerConfig.resetTimeout) {
      state.state = 'HALF_OPEN';
      state.failures = 0;
    }

    // If this is a failure that should trigger circuit breaker
    if (errorInfo.severity === ErrorSeverity.CRITICAL || 
        errorInfo.category === APIErrorCategory.SERVER_ERROR) {
      
      state.failures++;
      state.lastFailureTime = now;

      if (state.failures >= this.circuitBreakerConfig.failureThreshold) {
        state.state = 'OPEN';
      }
    } else if (errorInfo.category !== APIErrorCategory.CIRCUIT_BREAKER) {
      // Success case - reset circuit breaker
      state.failures = 0;
      state.state = 'CLOSED';
    }

    this.circuitBreakers.set(key, state);
  }

  /**
   * Get circuit breaker state for an endpoint
   */
  private getCircuitBreakerState(endpoint: string): 'CLOSED' | 'OPEN' | 'HALF_OPEN' {
    // Check if any circuit breaker for this endpoint is open
    for (const [key, state] of this.circuitBreakers.entries()) {
      if (key.startsWith(endpoint + ':') && state.state === 'OPEN') {
        return 'OPEN';
      }
    }
    return 'CLOSED';
  }

  /**
   * Update error statistics for monitoring
   */
  private updateErrorStats(errorInfo: APIErrorInfo): void {
    const key = `${errorInfo.context.endpoint}:${errorInfo.category}`;
    const stats = this.errorStats.get(key) || { count: 0, lastOccurrence: new Date() };
    
    stats.count++;
    stats.lastOccurrence = new Date();
    
    this.errorStats.set(key, stats);
  }

  /**
   * Log error for monitoring and debugging
   */
  private logError(errorInfo: APIErrorInfo): void {
    const logLevel = this.getLogLevel(errorInfo.severity);
    const logData = {
      category: errorInfo.category,
      severity: errorInfo.severity,
      code: errorInfo.code,
      message: errorInfo.message,
      endpoint: errorInfo.context.endpoint,
      method: errorInfo.context.method,
      userId: errorInfo.context.userId,
      establishmentId: errorInfo.context.establishmentId,
      retryCount: errorInfo.context.retryCount,
      fallbackUsed: errorInfo.context.fallbackUsed,
      responseTime: errorInfo.context.responseTime,
      metadata: errorInfo.metadata,
      timestamp: errorInfo.context.timestamp.toISOString()
    };

    switch (logLevel) {
      case 'error':
        logger.error('[APIErrorHandler]', new Error(logData.message), {
          module: 'api-error-handler',
          action: 'log-error',
          metadata: logData
        });
        break;
      case 'warn':
        logger.warn('[APIErrorHandler]', {
          module: 'api-error-handler',
          action: 'log-warning',
          metadata: logData
        });
        break;
      default:
        logger.info('[APIErrorHandler]', {
          module: 'api-error-handler',
          action: 'log-info',
          metadata: logData
        });
    }

    // Send to monitoring service if available
    if (typeof window !== 'undefined') {
      const win = window as { monitoringService?: { recordError: (data: typeof logData) => void } };
      if (win.monitoringService) {
        win.monitoringService.recordError(logData);
      }
    }
  }

  /**
   * Get log level based on error severity
   */
  private getLogLevel(severity: ErrorSeverity): 'error' | 'warn' | 'info' {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        return 'error';
      case ErrorSeverity.MEDIUM:
        return 'warn';
      default:
        return 'info';
    }
  }

  /**
   * Show user notification based on error info
   */
  private notifyUser(errorInfo: APIErrorInfo): void {
    // Don't show notifications for low severity errors or circuit breaker
    if (errorInfo.severity === ErrorSeverity.LOW || 
        errorInfo.category === APIErrorCategory.CIRCUIT_BREAKER) {
      return;
    }

    const options = {
      duration: this.getNotificationDuration(errorInfo.severity),
      icon: this.getNotificationIcon(errorInfo.category),
    };

    switch (errorInfo.severity) {
      case ErrorSeverity.CRITICAL:
        toast.error(errorInfo.userMessage, options);
        break;
      case ErrorSeverity.HIGH:
        toast.error(errorInfo.userMessage, options);
        break;
      case ErrorSeverity.MEDIUM:
        toast(errorInfo.userMessage, { ...options, icon: '⚠️' });
        break;
      default:
        toast(errorInfo.userMessage, options);
    }
  }

  /**
   * Get notification duration based on severity
   */
  private getNotificationDuration(severity: ErrorSeverity): number {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return 8000;
      case ErrorSeverity.HIGH:
        return 6000;
      case ErrorSeverity.MEDIUM:
        return 4000;
      default:
        return 3000;
    }
  }

  /**
   * Get notification icon based on error category
   */
  private getNotificationIcon(category: APIErrorCategory): string {
    switch (category) {
      case APIErrorCategory.NETWORK_ERROR:
        return '📡';
      case APIErrorCategory.TIMEOUT:
        return '⏱️';
      case APIErrorCategory.AUTHENTICATION:
        return '🔐';
      case APIErrorCategory.AUTHORIZATION:
        return '🚫';
      case APIErrorCategory.SERVER_ERROR:
        return '🔥';
      case APIErrorCategory.MAINTENANCE:
        return '🔧';
      case APIErrorCategory.RATE_LIMIT:
        return '⚡';
      default:
        return '❌';
    }
  }

  /**
   * Get comprehensive error statistics for monitoring dashboard
   */
  getErrorStatistics(): {
    total: number;
    byCategory: Record<APIErrorCategory, number>;
    bySeverity: Record<ErrorSeverity, number>;
    byEndpoint: Record<string, number>;
    circuitBreakerStatus: Record<string, string>;
    recentErrors: Array<{ endpoint: string; category: string; count: number; lastOccurrence: Date }>;
  } {
    const stats = {
      total: 0,
      byCategory: {} as Record<APIErrorCategory, number>,
      bySeverity: {} as Record<ErrorSeverity, number>,
      byEndpoint: {} as Record<string, number>,
      circuitBreakerStatus: {} as Record<string, string>,
      recentErrors: [] as Array<{ endpoint: string; category: string; count: number; lastOccurrence: Date }>
    };

    // Initialize counters
    Object.values(APIErrorCategory).forEach(category => {
      stats.byCategory[category] = 0;
    });
    Object.values(ErrorSeverity).forEach(severity => {
      stats.bySeverity[severity] = 0;
    });

    // Calculate statistics from error stats
    this.errorStats.forEach((value, key) => {
      const [endpoint, category] = key.split(':');
      if (!endpoint || !category || !isAPIErrorCategory(category)) {
        return;
      }

      stats.total += value.count;
      stats.byCategory[category] += value.count;
      stats.byEndpoint[endpoint] = (stats.byEndpoint[endpoint] || 0) + value.count;
      
      stats.recentErrors.push({
        endpoint,
        category,
        count: value.count,
        lastOccurrence: value.lastOccurrence
      });
    });

    // Circuit breaker status
    this.circuitBreakers.forEach((value, key) => {
      stats.circuitBreakerStatus[key] = value.state;
    });

    // Sort recent errors by last occurrence
    stats.recentErrors.sort((a, b) => b.lastOccurrence.getTime() - a.lastOccurrence.getTime());

    return stats;
  }

  /**
   * Reset error statistics (useful for testing or periodic cleanup)
   */
  resetStatistics(): void {
    this.errorStats.clear();
    this.circuitBreakers.clear();
  }

  /**
   * Update error handling strategy
   */
  updateStrategy(newStrategy: Partial<ErrorHandlingStrategy>): void {
    this.strategy = { ...this.strategy, ...newStrategy };
  }

  /**
   * Check if an error should trigger fallback
   */
  shouldFallback(errorInfo: APIErrorInfo): boolean {
    return this.strategy.enableFallback && errorInfo.shouldFallback;
  }

  /**
   * Check if an error should trigger retry
   */
  shouldRetry(errorInfo: APIErrorInfo, currentRetryCount: number): boolean {
    return errorInfo.shouldRetry && currentRetryCount < this.strategy.maxRetries;
  }
}

// Export singleton instance for global use
export const apiErrorHandler = APIErrorHandler.getInstance();

// Export default instance
export default apiErrorHandler;
